import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const CLASSIFICATIONS = [
  { value: "doenca_comum",                 label: "Doença comum" },
  { value: "acidente_trabalho",            label: "Acidente de trabalho" },
  { value: "acidente_trajeto",             label: "Acidente de trajeto" },
  { value: "suspeita_doenca_ocupacional",  label: "Suspeita de doença ocupacional" },
  { value: "consulta_medica",              label: "Consulta médica" },
  { value: "licenca_maternidade",          label: "Licença maternidade" },
  { value: "falta_justificada",            label: "Falta justificada" },
  { value: "falta_injustificada",          label: "Falta injustificada" },
  { value: "outros",                       label: "Outros" },
];
export const CLASS_LABEL = Object.fromEntries(CLASSIFICATIONS.map((c) => [c.value, c.label]));
export { CLASSIFICATIONS };

const PERIODS = [
  { value: "30",  label: "Últimos 30 dias" },
  { value: "90",  label: "Últimos 90 dias" },
  { value: "180", label: "Últimos 180 dias" },
  { value: "365", label: "Último ano" },
  { value: "all", label: "Todo o período" },
];

function fmtDate(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}
function today() { return new Date().toISOString().slice(0, 10); }

export default function Absenteeism({ companyIdOverride }) {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(companyIdOverride || "");
  const [period, setPeriod] = useState("90");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (companyIdOverride) { setCompanyId(companyIdOverride); return; }
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (r.data.length && !companyId) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, [companyIdOverride]);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    const params = { company_id: companyId };
    if (period !== "all") {
      params.start = fmtDate(parseInt(period, 10));
      params.end = today();
    }
    api.get("/absenteeism/dashboard", { params })
      .then((r) => setDashboard(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [companyId, period]);

  if (!companyIdOverride && !companies.length) {
    return (
      <div className="card text-center py-16">
        <i className="fa-solid fa-building text-5xl text-slate-300 mb-4"></i>
        <h2 className="font-display text-2xl font-extrabold">Nenhuma empresa cadastrada</h2>
        <p className="text-slate-600 mt-2">Cadastre uma empresa antes de começar a usar o módulo.</p>
        <Link to="/app/empresas" className="btn btn-primary mt-6 inline-flex">
          Ir para Empresas
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="absenteeism-page">
      <header>
        <div className="overline">Absenteísmo Inteligente NR-01</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Controle de Absenteísmo</h1>
        <p className="text-sm text-slate-600 mt-2 italic">
          Transformando afastamentos em inteligência preventiva.
        </p>
      </header>

      <div className="card border-l-4 border-amber-400 bg-amber-50/40" data-testid="lgpd-banner">
        <p className="text-sm text-amber-900">
          <i className="fa-solid fa-shield-halved mr-2"></i>
          <b>Aviso de confidencialidade.</b> Este módulo processa dados sensíveis de saúde dos
          colaboradores (atestados, CIDs). O acesso é restrito conforme a LGPD. Use o toggle
          &ldquo;Ocultar CIDs&rdquo; no relatório PDF para gerar versões administrativas seguras.
        </p>
      </div>

      <div className="card flex flex-wrap items-end gap-4">
        <label className="flex-1 min-w-[220px]">
          <div className="overline mb-1">Empresa</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                  data-testid="absenteeism-company-select"
                  className="w-full border border-slate-300 px-3 py-2">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <label className="min-w-[180px]">
          <div className="overline mb-1">Período</div>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
                  data-testid="absenteeism-period-select"
                  className="w-full border border-slate-300 px-3 py-2">
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <div className="flex gap-2 ml-auto">
          <Link to={`/app/absenteismo/colaboradores?empresa=${companyId}`} className="btn btn-ghost border border-slate-300" data-testid="absenteeism-employees-btn">
            <i className="fa-solid fa-users"></i> Colaboradores
          </Link>
          <Link to={`/app/absenteismo/atestados?empresa=${companyId}`} className="btn btn-ghost border border-slate-300" data-testid="absenteeism-certificates-btn">
            <i className="fa-solid fa-file-medical"></i> Atestados
          </Link>
          <Link to={`/app/absenteismo/inteligencia?empresa=${companyId}`} className="btn btn-ghost border border-emerald-300 text-emerald-900" data-testid="absenteeism-intelligence-btn">
            <i className="fa-solid fa-brain"></i> Inteligência IA
          </Link>
          <Link to={`/app/absenteismo/planos?empresa=${companyId}`} className="btn btn-ghost border border-slate-300" data-testid="absenteeism-plans-btn">
            <i className="fa-solid fa-list-check"></i> Plano de ação
          </Link>
          <Link to={`/app/absenteismo/importar?empresa=${companyId}`} className="btn btn-ghost border border-slate-300" data-testid="absenteeism-import-btn">
            <i className="fa-solid fa-file-excel"></i> Importar
          </Link>
          <button
            type="button"
            onClick={async () => {
              if (!companyId) return;
              try {
                const r = await api.get(`/absenteeism/export?company_id=${companyId}`, { responseType: "blob" });
                const url = window.URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `absenteismo-${companyId.split("-")[0]}.xlsx`;
                document.body.appendChild(a); a.click();
                a.remove(); window.URL.revokeObjectURL(url);
                toast.success("Planilha exportada. Edite e re-importe para atualizar.");
              } catch (err) {
                toast.error(err.response?.data?.detail || err.message);
              }
            }}
            className="btn btn-ghost border border-emerald-300 text-emerald-900"
            data-testid="absenteeism-export-btn"
          >
            <i className="fa-solid fa-file-export"></i> Exportar Excel
          </button>
          <Link to={`/app/absenteismo/relatorio?empresa=${companyId}`} className="btn btn-ghost border border-slate-300" data-testid="absenteeism-report-btn">
            <i className="fa-solid fa-file-pdf"></i> Relatório
          </Link>
          {companyId && (
            <Link to={`/app/empresas/${companyId}/mapa-risco`} className="btn btn-primary" data-testid="absenteeism-risk-map-btn">
              <i className="fa-solid fa-shield-halved"></i> Mapa de Risco
            </Link>
          )}
        </div>
      </div>

      {loading && <div className="card text-center text-slate-500">Carregando…</div>}
      {dashboard && !loading && <Dashboard d={dashboard} />}
    </div>
  );
}

function Dashboard({ d }) {
  const k = d.kpis;
  const cidF = useMemo(() => d.top_cids.filter((c) => c.group === "F"), [d.top_cids]);
  const cidM = useMemo(() => d.top_cids.filter((c) => c.group === "M"), [d.top_cids]);

  return (
    <>
      {/* KPIs */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="absenteeism-kpis">
        <KpiCard label="Total atestados"      value={k.total_certificates}      icon="fa-file-medical"     tone="emerald" />
        <KpiCard label="Dias perdidos"        value={k.total_days_lost}         icon="fa-calendar-xmark"   tone="rose" />
        <KpiCard label="Colaboradores afetados" value={k.employees_affected}    icon="fa-user-injured"     tone="amber" />
        <KpiCard label="Colaboradores ativos" value={k.active_employees}        icon="fa-user-check"       tone="slate" />
        <KpiCard label="Taxa de absenteísmo"  value={`${k.absenteeism_rate_pct}%`} icon="fa-percent"       tone={k.absenteeism_rate_pct > 4 ? "rose" : "emerald"}
                hint={k.absenteeism_rate_pct > 4 ? "Acima do benchmark (4%)" : "Dentro do benchmark"} />
      </div>

      {(cidF.length > 0 || cidM.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {cidF.length > 0 && (
            <InsightCard
              tone="rose"
              icon="fa-brain"
              title="CIDs F — saúde mental"
              body={`${cidF.reduce((s,c)=>s+c.count,0)} atestados relacionados a saúde mental. Sugestão: revisar fatores psicossociais (NR-01).`}
              testid="insight-cid-f"
            />
          )}
          {cidM.length > 0 && (
            <InsightCard
              tone="amber"
              icon="fa-bone"
              title="CIDs M — sistema musculoesquelético"
              body={`${cidM.reduce((s,c)=>s+c.count,0)} atestados relacionados a problemas musculoesqueléticos. Sugestão: avaliação ergonômica.`}
              testid="insight-cid-m"
            />
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <RankCard title="Afastamentos por Setor"   items={d.by_setor}   testid="rank-setor" />
        <RankCard title="Afastamentos por Cargo"   items={d.by_cargo}   testid="rank-cargo" />
        <RankCard title="Afastamentos por GHE"     items={d.by_ghe}     testid="rank-ghe" />
        <RankCard title="Tipos de afastamento"     items={d.by_classification.map((x)=>({...x, label: CLASS_LABEL[x.label] || x.label}))} testid="rank-tipo" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <CidsCard top={d.top_cids} />
        <ReincidenceCard top={d.top_reincidence} />
      </div>

      <MonthlySeriesCard series={d.monthly_series} />
    </>
  );
}

function KpiCard({ label, value, icon, tone, hint }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    rose:    "bg-rose-50    text-rose-900    border-rose-200",
    amber:   "bg-amber-50   text-amber-900   border-amber-200",
    slate:   "bg-slate-50   text-slate-900   border-slate-200",
  }[tone] || "bg-slate-50 text-slate-900 border-slate-200";
  return (
    <div className={`card border-2 ${tones}`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]/g,'')}`}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <i className={`fa-solid ${icon}`}></i>
      </div>
      <div className="font-display text-3xl font-extrabold mt-3">{value}</div>
      {hint && <div className="text-xs opacity-70 mt-1">{hint}</div>}
    </div>
  );
}

function InsightCard({ icon, title, body, tone, testid }) {
  const tones = {
    rose:  "border-rose-300 bg-rose-50/60 text-rose-900",
    amber: "border-amber-300 bg-amber-50/60 text-amber-900",
  }[tone] || "border-slate-300 bg-slate-50";
  return (
    <div className={`card border-l-4 ${tones}`} data-testid={testid}>
      <div className="flex items-start gap-3">
        <i className={`fa-solid ${icon} text-2xl`}></i>
        <div>
          <div className="font-display text-base font-extrabold">{title}</div>
          <p className="text-sm mt-1 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}

function RankCard({ title, items, testid }) {
  if (!items?.length) return <div className="card"><div className="overline">{title}</div><div className="text-sm text-slate-500 italic mt-2">Sem dados no período.</div></div>;
  const max = Math.max(...items.map((x) => x.days || x.count || 0), 1);
  return (
    <div className="card" data-testid={testid}>
      <div className="overline mb-4">{title}</div>
      <div className="space-y-2">
        {items.slice(0, 8).map((it, i) => (
          <div key={i}>
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium text-slate-800">{it.label}</span>
              <span className="text-slate-500"><b>{it.days}</b> dia(s) · {it.count} atestado(s)</span>
            </div>
            <div className="h-1.5 bg-slate-100 mt-1">
              <div className="h-full bg-emerald-700" style={{ width: `${(it.days / max) * 100}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CidsCard({ top }) {
  if (!top?.length) return <div className="card"><div className="overline">Ranking de CIDs</div><div className="text-sm text-slate-500 italic mt-2">Nenhum CID informado nos atestados.</div></div>;
  return (
    <div className="card" data-testid="rank-cids">
      <div className="overline mb-4">Ranking de CIDs</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
            <th className="text-left py-2">CID</th>
            <th className="text-left">Grupo</th>
            <th className="text-right">Casos</th>
            <th className="text-right">Dias</th>
          </tr>
        </thead>
        <tbody>
          {top.map((c) => (
            <tr key={c.cid} className="border-b border-slate-100">
              <td className="py-2 font-mono">{c.cid}</td>
              <td>{c.group ? <span className={`text-xs px-2 py-0.5 ${c.group === "F" ? "bg-rose-100 text-rose-900" : c.group === "M" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"}`}>{c.group}</span> : null}</td>
              <td className="text-right font-bold">{c.count}</td>
              <td className="text-right">{c.days}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReincidenceCard({ top }) {
  if (!top?.length) return <div className="card"><div className="overline">Reincidência</div><div className="text-sm text-slate-500 italic mt-2">Sem dados.</div></div>;
  return (
    <div className="card" data-testid="rank-reincidence">
      <div className="overline mb-4">Reincidência por colaborador</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
            <th className="text-left py-2">Colaborador</th>
            <th className="text-left">Setor</th>
            <th className="text-right">Atestados</th>
            <th className="text-right">Dias</th>
          </tr>
        </thead>
        <tbody>
          {top.map((r) => (
            <tr key={r.employee_id} className="border-b border-slate-100">
              <td className="py-2 font-medium">{r.nome || "—"}</td>
              <td className="text-slate-600">{r.setor || "—"}</td>
              <td className={`text-right font-bold ${r.count >= 3 ? "text-rose-700" : ""}`}>{r.count}</td>
              <td className="text-right">{r.days}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlySeriesCard({ series }) {
  if (!series?.length) return null;
  const maxDays = Math.max(...series.map((s) => s.days), 1);
  return (
    <div className="card" data-testid="monthly-series">
      <div className="overline mb-4">Evolução mensal (dias perdidos)</div>
      <div className="flex items-end gap-1 h-32">
        {series.map((s) => (
          <div key={s.month} className="flex-1 flex flex-col items-center group">
            <div className="text-[10px] font-bold text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">{s.days}d</div>
            <div className="w-full bg-emerald-700 hover:bg-emerald-800 transition-colors min-h-[2px]"
                 style={{ height: `${(s.days / maxDays) * 100}%` }}
                 title={`${s.month}: ${s.days} dias, ${s.count} atestados`}>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{s.month.slice(5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
